interface EasyAppointmentsService {
  id: string;
  name: string;
  duration: number;
  price?: number;
  description?: string;
}

interface EasyAppointmentsProvider {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
}

interface AvailabilitySlot {
  start: string;
  end: string;
}

interface BookingRequest {
  service_id: string;
  provider_id: string;
  start_datetime: string;
  end_datetime: string;
  customer: {
    first_name: string;
    last_name: string;
    email: string;
    phone: string;
  };
  notes?: string;
}

interface BookingResponse {
  id: string | number;
  hash: string;
  book: string;
  start: string;
  end: string;
  notes?: string;
  customerId: number;
  providerId: number;
  serviceId: number;
  color?: string;
  status?: string;
  location?: string;
  googleCalendarId?: string;
  caldavCalendarId?: string;
  // Helper properties für UI-Anzeige (werden dynamisch hinzugefügt)
  customer?: {
    id: string;
    first_name: string;
    last_name: string;
    email: string;
    phone: string;
  };
  service?: {
    id: string;
    name: string;
    duration: number;
  };
  provider?: {
    id: string;
    first_name: string;
    last_name: string;
  };
}

class AppointmentsApi {
  private baseUrl: string;

  constructor() {
    // Production: API-Proxy Server
    // Für Production sollte dies über eine Environment Variable konfiguriert werden
    this.baseUrl = import.meta.env.VITE_API_PROXY_URL || 'http://localhost:3001';
  }

  private async request(endpoint: string, options: RequestInit = {}): Promise<unknown> {
    const url = `${this.baseUrl}${endpoint}`;
    
    console.log('API Request Details:', { 
      url, 
      endpoint,
      baseUrl: this.baseUrl,
      options 
    });
    
    const response = await fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        // Kein Authorization Header mehr - wird im Server gehandhabt
        ...options.headers,
      },
    });

    const responseText = await response.text();
    console.log('API Response:', { status: response.status, body: responseText });

    if (!response.ok) {
      throw new Error(`API Error: ${response.status} ${response.statusText} - ${responseText}`);
    }

    try {
      return JSON.parse(responseText);
    } catch {
      return responseText;
    }
  }

  async getServices(): Promise<EasyAppointmentsService[]> {
    try {
      const services = await this.request('/api/appointments/services') as EasyAppointmentsService[];
      console.log('Fetched services from API:', services);
      return services.filter((service: EasyAppointmentsService) => 
        service.name.toLowerCase().includes('beratung') || 
        service.name.toLowerCase().includes('erstberatung') ||
        service.name.toLowerCase().includes('erstgespräch')
      );
    } catch (error) {
      console.error('Failed to fetch services:', error);
      // Fallback zu Demo-Daten mit korrekter ID
      return [
        {
          id: '1', // Korrekte Service-ID vom Server
          name: 'Kostenfreies Erstgespräch',
          duration: 30,
          price: 0,
          description: '30 Minuten kostenlose Beratung für Ihr Digitalprojekt'
        }
      ];
    }
  }

  async getProviders(): Promise<EasyAppointmentsProvider[]> {
    try {
      const providers = await this.request('/api/appointments/providers') as EasyAppointmentsProvider[];
      console.log('Fetched providers from API:', providers);
      return providers;
    } catch (error) {
      console.error('Failed to fetch providers:', error);
      // Fallback zu Demo-Daten mit korrekter ID
      return [
        {
          id: '4', // Korrekte Provider-ID vom Server
          firstName: 'Henri',
          lastName: 'Stimm',
          email: 'henri@stimm.dev'
        }
      ];
    }
  }

  async getAvailabilities(
    serviceId: string,
    providerId: string,
    date: string
  ): Promise<AvailabilitySlot[]> {
    try {
      // Validate inputs before making API call
      if (!serviceId || !providerId || !date) {
        console.warn('Missing required parameters for availabilities:', { serviceId, providerId, date });
        return this.generateDemoSlots(date);
      }
      
      // Ensure IDs are properly formatted as integers
      const serviceIdInt = parseInt(serviceId, 10);
      const providerIdInt = parseInt(providerId, 10);
      
      if (isNaN(serviceIdInt) || isNaN(providerIdInt)) {
        console.warn('Invalid service or provider ID:', { serviceId, providerId });
        return this.generateDemoSlots(date);
      }
      
      // Validate that IDs are positive integers
      if (serviceIdInt <= 0 || providerIdInt <= 0) {
        console.warn('Service and Provider IDs must be positive integers:', { serviceIdInt, providerIdInt });
        return this.generateDemoSlots(date);
      }
      
      console.log('Making availabilities API call with:', { serviceIdInt, providerIdInt, date });
      
      // Use the working endpoint format
      const endpoint = `/api/appointments/availabilities?serviceId=${serviceIdInt}&providerId=${providerIdInt}&date=${date}`;
      // console.log(`Using endpoint: ${endpoint}`);
      
      const response = await this.request(endpoint);
      // console.log('Raw API response:', response);
      
      // Handle different response formats
      if (Array.isArray(response)) {
        // Response is directly an array of time strings
        console.log('API returned time array:', response);
        return this.convertTimeArrayToSlots(response as string[], date);
      } else if (response && typeof response === 'object') {
        const responseObj = response as { availabilities?: AvailabilitySlot[]; success?: boolean; message?: string };
        if (responseObj.availabilities) {
          return responseObj.availabilities;
        }
      }
      
      // If we get here, no valid data was returned
      console.warn('No valid availability data returned from API');
      return this.generateDemoSlots(date);
    } catch (error) {
      console.error('Failed to fetch availabilities:', error);
      // Fallback zu Demo-Daten
      return this.generateDemoSlots(date);
    }
  }

  private convertTimeArrayToSlots(times: string[], date: string): AvailabilitySlot[] {
    return times.map(time => {
      const [hours, minutes] = time.split(':');
      const startTime = `${date} ${time}:00`;
      
      // Add 30 minutes for end time (standard appointment duration)
      const endDate = new Date(`${date}T${hours}:${minutes}`);
      endDate.setMinutes(endDate.getMinutes() + 30);
      const endTime = `${date} ${endDate.getHours().toString().padStart(2, '0')}:${endDate.getMinutes().toString().padStart(2, '0')}:00`;
      
      return {
        start: startTime,
        end: endTime
      };
    });
  }

  private generateDemoSlots(date: string): AvailabilitySlot[] {
    const slots: AvailabilitySlot[] = [];
    const times = [
      '09:00', '09:30', '10:00', '10:30', '11:00', '11:30',
      '14:00', '14:30', '15:00', '15:30', '16:00', '16:30'
    ];

    times.forEach((time) => {
      // Randomly make some slots unavailable for demo
      if (Math.random() > 0.3) {
        const [hours, minutes] = time.split(':');
        const startTime = `${date} ${time}:00`;
        const endDate = new Date(`${date}T${hours}:${minutes}`);
        endDate.setMinutes(endDate.getMinutes() + 30);
        const endTime = `${date} ${endDate.getHours().toString().padStart(2, '0')}:${endDate.getMinutes().toString().padStart(2, '0')}:00`;
        
        slots.push({
          start: startTime,
          end: endTime
        });
      }
    });

    return slots;
  }

  async bookAppointment(booking: BookingRequest): Promise<BookingResponse> {
    if (!this.isApiConfigured()) {
      throw new Error('Buchungssystem ist nicht konfiguriert. Bitte kontaktieren Sie uns direkt.');
    }

    try {
      // Erste Strategie: Customer zuerst erstellen, dann Appointment
      // Das entspricht dem EasyAppointments Standard-Workflow
      
      // 1. Customer erstellen/finden
      const customerRequest = {
        firstName: booking.customer.first_name,
        lastName: booking.customer.last_name,
        email: booking.customer.email,
        phone: booking.customer.phone // EasyAppointments API verwendet 'phone'
      };
      
      console.log('Creating customer first:', customerRequest);
      
      // Versuche zuerst Customer zu erstellen
      let customerId: number;
      try {
        const customerResponse = await this.request('/api/customers', {
          method: 'POST',
          body: JSON.stringify(customerRequest),
        }) as { id: number };
        customerId = customerResponse.id;
        console.log('Customer created with ID:', customerId);
      } catch (customerError) {
        console.log('Customer creation failed, will try direct appointment booking:', customerError);
        // Fallback zur direkten Appointment-Buchung mit eingebetteten Customer-Daten
        return this.bookAppointmentDirect(booking);
      }
      
      // 2. Appointment mit Customer-ID erstellen
      const appointmentRequest = {
        book: new Date().toISOString().slice(0, 19).replace('T', ' '),
        start: booking.start_datetime,
        end: booking.end_datetime,
        notes: booking.notes || '',
        serviceId: parseInt(booking.service_id, 10),
        providerId: parseInt(booking.provider_id, 10),
        customerId: customerId
      };
      
      console.log('Creating appointment with customer ID:', appointmentRequest);
      
      const response = await this.request('/api/appointments', {
        method: 'POST',
        body: JSON.stringify(appointmentRequest),
      }) as BookingResponse;
      
      console.log('Booking successful:', response);
      
      // Enrich response with additional data for UI display
      const enrichedResponse: BookingResponse = {
        ...response,
        customer: {
          id: customerId.toString(),
          first_name: booking.customer.first_name,
          last_name: booking.customer.last_name,
          email: booking.customer.email,
          phone: booking.customer.phone,
        }
      };
      
      // Add service and provider data if we have them
      try {
        const [services, providers] = await Promise.all([
          this.getServices(),
          this.getProviders()
        ]);
        
        const service = services.find(s => s.id === booking.service_id);
        const provider = providers.find(p => p.id === booking.provider_id);
        
        if (service) {
          enrichedResponse.service = {
            id: service.id,
            name: service.name,
            duration: service.duration
          };
        }
        
        if (provider) {
          enrichedResponse.provider = {
            id: provider.id,
            first_name: provider.firstName,
            last_name: provider.lastName
          };
        }
      } catch (error) {
        console.warn('Failed to enrich booking response with service/provider data:', error);
      }
      
      return enrichedResponse;
      
    } catch (error) {
      console.error('Failed to book appointment:', error);
      
      // Check for specific server errors
      if (error instanceof Error) {
        if (error.message.includes('syntax error') || 
            error.message.includes('ParseError') || 
            error.message.includes('500')) {
          throw new Error('Server-Fehler: Das Buchungssystem ist temporär nicht verfügbar. Bitte kontaktieren Sie uns direkt.');
        }
        if (error.message.includes('network') || error.message.includes('fetch')) {
          throw new Error('Netzwerkfehler: Bitte überprüfen Sie Ihre Internetverbindung.');
        }
      }
      
      // Re-throw the original error
      throw error;
    }
  }

  // Fallback-Methode für direkte Appointment-Buchung
  private async bookAppointmentDirect(booking: BookingRequest): Promise<BookingResponse> {
    // Direkte Appointment-Buchung mit Customer-Daten
    // Dies entspricht dem Format, wie es der Booking-Controller verwendet
    const appointmentRequest = {
      book: new Date().toISOString().slice(0, 19).replace('T', ' '),
      start: booking.start_datetime,
      end: booking.end_datetime,
      notes: booking.notes || '',
      serviceId: parseInt(booking.service_id, 10),
      providerId: parseInt(booking.provider_id, 10),
      // Customer data direkt einbetten (wie im Booking-Controller)
      firstName: booking.customer.first_name,
      lastName: booking.customer.last_name,
      email: booking.customer.email,
      phone: booking.customer.phone
    };
    
    console.log('Direct appointment booking with embedded customer data:', appointmentRequest);
    
    const response = await this.request('/api/appointments', {
      method: 'POST',
      body: JSON.stringify(appointmentRequest),
    });
    
    return response as BookingResponse;
  }

  isApiConfigured(): boolean {
    return !!this.baseUrl;
  }
}

export const appointmentsApi = new AppointmentsApi();

export type {
  EasyAppointmentsService,
  EasyAppointmentsProvider,
  AvailabilitySlot,
  BookingRequest,
  BookingResponse
};
